import React, { useState, useRef, useEffect } from 'react';
import { useCrowd } from './context/CrowdContext';
import { fmt, losColor, crowdWord } from './utils/crowdMath';
import {
  X,
  Camera,
  Cctv,
  Flame,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Info,
  Eye,
  ShoppingBag,
  Maximize2,
  ChevronDown,
  ChevronUp,
  Video,
} from 'lucide-react';

interface PrasadamBlueprintModalProps {
  onClose: () => void;
  onOpenCamera?: (camId: string) => void;
}

interface CounterData {
  id: number;
  label: string;
  sublabel?: string;
  isPriority?: boolean;
  x: number;
  w: number;
  h: number;
  count: number;
  capacity: number;
  density: number;
  ratePerMin: number;
  camId: string;
}

// =========================================================================
// REUSABLE REAL-TIME AI CCTV CANVAS STREAM PLAYER
// =========================================================================
interface PrasadamLiveCameraCanvasProps {
  counter: CounterData;
  isCompact?: boolean;
}

const PrasadamLiveCameraCanvas: React.FC<PrasadamLiveCameraCanvasProps> = ({
  counter,
  isCompact = false
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;

    // Fixed crisp internal resolution - never reallocated inside the frame loop
    const W = isCompact ? 720 : 1280;
    const H = isCompact ? 405 : 720;
    cv.width = W;
    cv.height = H;

    interface DevoteeParticle {
      id: number;
      lane: 'in' | 'counter' | 'out';
      x: number;
      y: number;
      targetX: number;
      speed: number;
      dwell: number;
      color: string;
      conf: string;
      hasLaddu: boolean;
      stepPhase: number;
      offset: number;
    }

    const devoteeColors = [
      '#EA580C', // Saffron / Kesari
      '#D97706', // Turmeric Ochre
      '#F8FAFC', // Holy White Kurta
      '#0284C7', // Sky Blue
      '#B91C1C', // Temple Maroon
      '#F59E0B', // Golden Yellow
      '#059669', // Emerald Green
      '#7C3AED', // Deep Violet
      '#E2E8F0', // Cream Silk
      '#F43F5E'  // Rose Red
    ];

    const particles: DevoteeParticle[] = [];
    const targetDevotees = isCompact ? 10 : 20;

    // Seed devotees evenly spread across the queue and exit channels
    for (let i = 0; i < targetDevotees; i++) {
      const isEgress = i % 3 === 0;
      const offset = (Math.random() - 0.5) * 0.08;
      const initialY = 0.32 + (i / targetDevotees) * 0.68;
      const lane = isEgress ? 'out' : (initialY < 0.36 && i % 4 === 1 ? 'counter' : 'in');
      const targetX = lane === 'out' ? 0.26 + offset : 0.72 + offset;

      particles.push({
        id: i + 1,
        lane,
        x: targetX,
        y: initialY,
        targetX,
        speed: 0.00042 + Math.random() * 0.00022, // Realistic smooth walking speed
        dwell: Math.floor(120 + Math.random() * 120),
        color: devoteeColors[i % devoteeColors.length],
        conf: (0.92 + Math.random() * 0.06).toFixed(2),
        hasLaddu: lane === 'out' || lane === 'counter',
        stepPhase: Math.random() * Math.PI * 2,
        offset
      });
    }

    let animId: number;
    let fpsVal = 30.0;
    let frameCount = 0;
    let fpsTimer = performance.now();

    const render = () => {
      const ctx = cv.getContext('2d');
      if (!ctx) return;

      const nowTime = performance.now();
      frameCount++;
      if (nowTime - fpsTimer >= 500) {
        fpsVal = (frameCount * 1000) / (nowTime - fpsTimer);
        frameCount = 0;
        fpsTimer = nowTime;
      }

      // 1. Scene Background (Dark Surveillance Concourse)
      ctx.fillStyle = '#060E18';
      ctx.fillRect(0, 0, W, H);

      // Counter Back Wall
      const horizonY = H * 0.28;
      ctx.fillStyle = '#0B1826';
      ctx.fillRect(0, 0, W, horizonY);

      // 2. Stainless Steel Counter Desk & Granite Top
      ctx.fillStyle = '#111D2D';
      ctx.fillRect(W * 0.06, horizonY - 32, W * 0.88, 36);
      ctx.strokeStyle = '#D97706';
      ctx.lineWidth = 2;
      ctx.strokeRect(W * 0.06, horizonY - 32, W * 0.88, 36);

      // Acrylic service pass-through windows
      const windowCount = isCompact ? 5 : 9;
      for (let wIdx = 0; wIdx < windowCount; wIdx++) {
        const wx = W * 0.08 + wIdx * (W * (isCompact ? 0.17 : 0.095));
        ctx.fillStyle = 'rgba(56, 189, 248, 0.12)';
        ctx.fillRect(wx, horizonY - 26, isCompact ? 34 : 48, 20);
        ctx.strokeStyle = 'rgba(56, 189, 248, 0.35)';
        ctx.lineWidth = 1;
        ctx.strokeRect(wx, horizonY - 26, isCompact ? 34 : 48, 20);
      }

      // Overhead Signboard
      ctx.fillStyle = '#78350F';
      ctx.fillRect(W * 0.26, horizonY - 62, W * 0.48, 26);
      ctx.strokeStyle = '#F59E0B';
      ctx.lineWidth = 1.8;
      ctx.strokeRect(W * 0.26, horizonY - 62, W * 0.48, 26);
      ctx.fillStyle = '#FDE68A';
      ctx.font = 'bold 12px "Outfit", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(
        counter.id === 12
          ? 'COUNTER 12 · PHYSICALLY HANDICAPPED PRIORITY PRASADAM'
          : `LADDU & PRASADAM DISTRIBUTION · COUNTER ${counter.label}`,
        W * 0.5,
        horizonY - 45
      );

      // Trays of Golden Laddus stacked on counter desk
      const trayCount = isCompact ? 5 : 8;
      for (let tx = 0; tx < trayCount; tx++) {
        const trayX = W * 0.10 + tx * (W * (isCompact ? 0.16 : 0.10));
        ctx.fillStyle = '#1E293B';
        ctx.fillRect(trayX, horizonY - 14, isCompact ? 36 : 46, 12);
        ctx.strokeStyle = '#475569';
        ctx.lineWidth = 1;
        ctx.strokeRect(trayX, horizonY - 14, isCompact ? 36 : 46, 12);
        // Golden laddu mounds
        for (let lx = 0; lx < (isCompact ? 3 : 4); lx++) {
          ctx.beginPath();
          ctx.arc(trayX + 6 + lx * (isCompact ? 8 : 10), horizonY - 8, isCompact ? 4 : 5, 0, Math.PI * 2);
          ctx.fillStyle = '#F59E0B';
          ctx.fill();
        }
      }

      // 3. Queue Stanchions and Floor In/Out Lanes
      // Central Stainless Steel Barricade Divider
      ctx.strokeStyle = '#475569';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(W * 0.49, horizonY);
      ctx.lineTo(W * 0.49, H);
      ctx.stroke();

      // Outer safety boundary barricades
      ctx.strokeStyle = '#334155';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(W * 0.08, horizonY);
      ctx.lineTo(W * 0.08, H);
      ctx.moveTo(W * 0.92, horizonY);
      ctx.lineTo(W * 0.92, H);
      ctx.stroke();

      // Floor Lane Markers
      ctx.fillStyle = 'rgba(56, 189, 248, 0.05)';
      ctx.fillRect(W * 0.50, horizonY, W * 0.42, H - horizonY); // IN queue lane
      ctx.fillStyle = 'rgba(245, 158, 11, 0.05)';
      ctx.fillRect(W * 0.08, horizonY, W * 0.41, H - horizonY); // OUT egress lane

      // Lane Guide Markings on Floor
      ctx.font = 'bold 12px "Outfit", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(56, 189, 248, 0.22)';
      ctx.fillText('▲ INGRESS QUEUE (క్యూ లైన్)', W * 0.72, H * 0.62);
      ctx.fillStyle = 'rgba(245, 158, 11, 0.22)';
      ctx.fillText('▼ EGRESS WITH PRASADAM (నిష్క్రమణ)', W * 0.26, H * 0.62);

      // Directional Floor Chevrons
      const chevronY = (nowTime * 0.02) % 40;
      ctx.strokeStyle = 'rgba(56, 189, 248, 0.15)';
      ctx.lineWidth = 2;
      for (let cy = H * 0.4; cy < H * 0.9; cy += 40) {
        const yPos = cy - chevronY;
        if (yPos > horizonY + 20 && yPos < H - 20) {
          ctx.beginPath();
          ctx.moveTo(W * 0.68, yPos + 8);
          ctx.lineTo(W * 0.72, yPos);
          ctx.lineTo(W * 0.76, yPos + 8);
          ctx.stroke();
        }
      }

      ctx.strokeStyle = 'rgba(245, 158, 11, 0.15)';
      for (let cy = horizonY + 20; cy < H * 0.85; cy += 40) {
        const yPos = cy + chevronY;
        if (yPos > horizonY + 20 && yPos < H - 20) {
          ctx.beginPath();
          ctx.moveTo(W * 0.22, yPos);
          ctx.lineTo(W * 0.26, yPos + 8);
          ctx.lineTo(W * 0.30, yPos);
          ctx.stroke();
        }
      }

      // Sort devotees by Y for proper perspective overlap (back to front)
      particles.sort((a, b) => a.y - b.y);

      // 4. Update and Draw Devotees
      particles.forEach(p => {
        // Continuous smooth movement
        if (p.lane === 'in') {
          // Devotee walks forward in ingress queue towards counter
          p.x += (p.targetX - p.x) * 0.02;
          p.y -= p.speed;
          p.stepPhase += p.speed * 60;

          if (p.y <= 0.32) {
            // Reached counter hatch: transition to collecting state
            p.y = 0.32;
            p.lane = 'counter';
            p.dwell = Math.floor(160 + Math.random() * 100); // 2.5 - 4 seconds dwell
            p.targetX = 0.58 + (Math.random() - 0.5) * 0.06;
          }
        } else if (p.lane === 'counter') {
          // Devotee receives laddu boxes at counter
          p.dwell -= 1;
          p.x += (p.targetX - p.x) * 0.03;
          p.stepPhase += 0.02; // Gentle posture shift while standing

          if (p.dwell <= 0) {
            // Finished collecting: transition to egress lane
            p.lane = 'out';
            p.hasLaddu = true;
            p.targetX = 0.26 + p.offset;
          }
        } else if (p.lane === 'out') {
          // Devotee walks down egress corridor with laddu prasad
          p.x += (p.targetX - p.x) * 0.025;
          p.y += p.speed * 1.15;
          p.stepPhase += p.speed * 70;

          if (p.y >= 1.06) {
            // Smoothly recycle to bottom of queue
            p.y = 1.02 + Math.random() * 0.05;
            p.lane = 'in';
            p.hasLaddu = false;
            p.offset = (Math.random() - 0.5) * 0.08;
            p.targetX = 0.72 + p.offset;
            p.speed = 0.00042 + Math.random() * 0.00022;
            p.conf = (0.92 + Math.random() * 0.06).toFixed(2);
          }
        }

        const px = p.x * W;
        const py = p.y * H;
        const depth = Math.max(0, Math.min(1, (py - horizonY) / (H - horizonY)));
        const scale = (isCompact ? 0.75 : 0.90) + depth * (isCompact ? 0.45 : 0.65);

        // Ground shadow
        ctx.beginPath();
        ctx.ellipse(px, py + 14 * scale, 12 * scale, 4.5 * scale, 0, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
        ctx.fill();

        // Alternating Walking Legs
        const legPhase = p.lane === 'counter' ? 0 : Math.sin(p.stepPhase) * 4 * scale;
        ctx.strokeStyle = '#334155';
        ctx.lineWidth = Math.max(1.8, 2.4 * scale);
        // Left Leg
        ctx.beginPath();
        ctx.moveTo(px - 4 * scale, py + 4 * scale);
        ctx.lineTo(px - 4 * scale + legPhase, py + 14 * scale);
        ctx.stroke();
        // Right Leg
        ctx.beginPath();
        ctx.moveTo(px + 4 * scale, py + 4 * scale);
        ctx.lineTo(px + 4 * scale - legPhase, py + 14 * scale);
        ctx.stroke();

        // Devotee Body / Attire (Kurta / Dhoti / Sari)
        const bodySway = p.lane === 'counter' ? 0 : Math.sin(p.stepPhase * 0.5) * 1.5 * scale;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.moveTo(px - 10 * scale + bodySway, py + 8 * scale);
        ctx.lineTo(px - 8 * scale + bodySway, py - 12 * scale);
        ctx.lineTo(px + 8 * scale + bodySway, py - 12 * scale);
        ctx.lineTo(px + 10 * scale + bodySway, py + 8 * scale);
        ctx.closePath();
        ctx.fill();

        // Devotee Head & Neck
        ctx.beginPath();
        ctx.arc(px + bodySway, py - 20 * scale, 7.5 * scale, 0, Math.PI * 2);
        ctx.fillStyle = '#FED7AA';
        ctx.fill();

        // Tilak mark on forehead
        ctx.fillStyle = '#DC2626';
        ctx.fillRect(px + bodySway - 0.8 * scale, py - 23 * scale, 1.6 * scale, 3 * scale);

        // Golden Prasadam Bag in hand (visible when exiting or counter collecting)
        if (p.hasLaddu) {
          const bagX = px + 10 * scale + bodySway;
          const bagY = py - 2 * scale;
          // Yellow prasadam bag
          ctx.fillStyle = '#F59E0B';
          ctx.fillRect(bagX, bagY, 9 * scale, 11 * scale);
          ctx.strokeStyle = '#D97706';
          ctx.lineWidth = 1;
          ctx.strokeRect(bagX, bagY, 9 * scale, 11 * scale);
          // Bag handles
          ctx.strokeStyle = '#B45309';
          ctx.beginPath();
          ctx.arc(bagX + 4.5 * scale, bagY, 3 * scale, Math.PI, 0);
          ctx.stroke();
        }

        // 5. AI Computer Vision Detection Bounding Box
        const boxW = 32 * scale;
        const boxH = 54 * scale;
        const bx = px - boxW / 2 + bodySway;
        const by = py - 30 * scale;

        const boxColor = p.lane === 'in' ? '#00F0B5' : p.lane === 'counter' ? '#F59E0B' : '#38BDF8';
        ctx.strokeStyle = boxColor;
        ctx.lineWidth = 1.3;
        ctx.strokeRect(bx, by, boxW, boxH);

        // Precision corner brackets
        const tick = 5 * scale;
        ctx.beginPath();
        ctx.moveTo(bx, by + tick); ctx.lineTo(bx, by); ctx.lineTo(bx + tick, by);
        ctx.moveTo(bx + boxW - tick, by); ctx.lineTo(bx + boxW, by); ctx.lineTo(bx + boxW, by + tick);
        ctx.moveTo(bx, by + boxH - tick); ctx.lineTo(bx, by + boxH); ctx.lineTo(bx + tick, by + boxH);
        ctx.moveTo(bx + boxW - tick, by + boxH); ctx.lineTo(bx + boxW, by + boxH); ctx.lineTo(bx + boxW, by + boxH - tick);
        ctx.stroke();

        // AI classification tag
        const tagText = p.lane === 'in' ? `IN ${p.conf}` : p.lane === 'counter' ? `CTR ${p.conf}` : `OUT ${p.conf}`;
        const tagH = isCompact ? 10 : 13;
        const tagW = boxW;
        ctx.fillStyle = p.lane === 'in' ? 'rgba(0, 240, 181, 0.90)' : p.lane === 'counter' ? 'rgba(245, 158, 11, 0.90)' : 'rgba(56, 189, 248, 0.90)';
        ctx.fillRect(bx, by - tagH, tagW, tagH);
        ctx.fillStyle = '#040D18';
        ctx.font = `bold ${isCompact ? '7px' : '9px'} monospace`;
        ctx.textAlign = 'center';
        ctx.fillText(tagText, bx + tagW / 2, by - 3);
      });

      // 6. CCTV Camera Overlays & HUD (Smooth, zero re-renders)
      // Subtle static scanlines
      ctx.fillStyle = 'rgba(255, 255, 255, 0.015)';
      for (let sl = 0; sl < H; sl += 4) {
        ctx.fillRect(0, sl, W, 1);
      }

      // Smooth pulsing REC dot (breathing effect without hard flickering)
      const pulse = 0.4 + 0.6 * Math.abs(Math.sin(nowTime * 0.003));
      ctx.fillStyle = `rgba(239, 68, 68, ${pulse})`;
      ctx.beginPath();
      ctx.arc(isCompact ? 16 : 24, isCompact ? 16 : 24, isCompact ? 4 : 5.5, 0, Math.PI * 2);
      ctx.fill();

      // Top-Left Recording Status
      ctx.fillStyle = '#FFFFFF';
      ctx.font = `bold ${isCompact ? '10px' : '12px'} monospace`;
      ctx.textAlign = 'left';
      ctx.fillText('REC', isCompact ? 26 : 38, isCompact ? 20 : 28);
      ctx.fillStyle = '#10B981';
      ctx.fillText(isCompact ? '● ONLINE' : '● ONLINE · LIVE AI STREAM', isCompact ? 58 : 78, isCompact ? 20 : 28);

      // Top-Right Camera ID & Live IST Timestamp
      const now = new Date();
      const utc = now.getTime() + now.getTimezoneOffset() * 60000;
      const ist = new Date(utc + 3600000 * 5.5);
      const timeStr = `${ist.getFullYear()}-${String(ist.getMonth() + 1).padStart(2, '0')}-${String(ist.getDate()).padStart(2, '0')} ` +
        `${String(ist.getHours()).padStart(2, '0')}:${String(ist.getMinutes()).padStart(2, '0')}:${String(ist.getSeconds()).padStart(2, '0')} IST`;

      ctx.textAlign = 'right';
      ctx.fillStyle = '#38BDF8';
      ctx.fillText(`${counter.camId}`, W - (isCompact ? 12 : 20), isCompact ? 20 : 28);
      if (!isCompact) {
        ctx.fillStyle = '#CBD5E1';
        ctx.fillText(timeStr, W - 20, 46);
        ctx.fillStyle = '#94A3B8';
        ctx.font = '11px monospace';
        ctx.fillText(`${Math.round(fpsVal)} FPS · 1080P · 4.8 Mbps`, W - 20, 64);
      }

      // Bottom-Left Telemetry Box
      ctx.textAlign = 'left';
      const bH = isCompact ? 26 : 38;
      const bW = isCompact ? 175 : 240;
      ctx.fillStyle = 'rgba(6, 14, 24, 0.85)';
      ctx.fillRect(isCompact ? 10 : 16, H - (bH + 10), bW, bH);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)';
      ctx.lineWidth = 1;
      ctx.strokeRect(isCompact ? 10 : 16, H - (bH + 10), bW, bH);

      ctx.fillStyle = '#00F0B5';
      ctx.font = `bold ${isCompact ? '9px' : '11px'} monospace`;
      ctx.fillText(`PAX IN CHANNEL: ${counter.count}`, isCompact ? 16 : 24, H - (isCompact ? 20 : 32));
      ctx.fillStyle = '#F59E0B';
      ctx.fillText(`CLEARANCE: ${counter.ratePerMin} LADDUS/MIN`, isCompact ? 16 : 24, H - (isCompact ? 9 : 18));

      animId = requestAnimationFrame(render);
    };

    render();
    return () => cancelAnimationFrame(animId);
  }, [counter.id, isCompact]);

  return (
    <canvas
      ref={canvasRef}
      width={isCompact ? 720 : 1280}
      height={isCompact ? 405 : 720}
      className="w-full h-full block object-cover"
    />
  );
};

// =========================================================================
// PICTURE-IN-PICTURE DOCKED LIVE CCTV FEED WIDGET
// =========================================================================
interface PrasadamDockedCameraFeedProps {
  counter: CounterData;
  allCounters: CounterData[];
  onSelectCounter: (counter: CounterData) => void;
  onMaximize: (counter: CounterData) => void;
}

const PrasadamDockedCameraFeed: React.FC<PrasadamDockedCameraFeedProps> = ({
  counter,
  allCounters,
  onSelectCounter,
  onMaximize
}) => {
  const [isMinimized, setIsMinimized] = useState<boolean>(false);

  if (isMinimized) {
    return (
      <div className="absolute bottom-3 right-3 z-30 flex items-center gap-2 bg-white/95 dark:bg-[#181b22]/95 border border-neutral-200/80 dark:border-[#262b37] rounded-lg p-1.5 shadow-xl backdrop-blur-md animate-in slide-in-from-bottom-2">
        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse ml-1" />
        <span className="font-mono text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
          {counter.camId} · ONLINE
        </span>
        <span className="text-[11px] font-bold text-neutral-800 dark:text-neutral-100">
          Counter {counter.label}
        </span>
        <button
          onClick={() => setIsMinimized(false)}
          className="p-1 rounded-md bg-neutral-100 dark:bg-[#222733] hover:bg-neutral-200 dark:hover:bg-[#2c3242] text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition cursor-pointer"
          title="Restore Live CCTV Stream View"
        >
          <ChevronUp className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onMaximize(counter)}
          className="p-1 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-600 dark:text-emerald-400 transition cursor-pointer"
          title="Fullscreen Camera Feed"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="absolute bottom-3 right-3 z-30 w-[330px] sm:w-[370px] bg-white/95 dark:bg-[#181b22]/95 border border-neutral-200/80 dark:border-[#262b37] rounded-xl shadow-2xl backdrop-blur-md overflow-hidden flex flex-col animate-in slide-in-from-bottom-3 duration-200">
      <div className="flex items-center justify-between px-3 py-1.5 bg-neutral-50/80 dark:bg-[#14171f] border-b border-neutral-200/60 dark:border-[#222733]">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
          <span className="font-mono text-[10.5px] font-bold text-emerald-600 dark:text-emerald-400">
            {counter.camId} · ONLINE
          </span>
          <span className="text-[11px] font-bold text-neutral-800 dark:text-neutral-100 truncate max-w-[140px]">
            Counter {counter.label} {counter.id === 12 ? '(PH)' : ''}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => onMaximize(counter)}
            className="p-1 rounded-md hover:bg-neutral-100 dark:hover:bg-[#222733] text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition cursor-pointer"
            title="Expand to Fullscreen CCTV Modal"
          >
            <Maximize2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
          </button>
          <button
            onClick={() => setIsMinimized(true)}
            className="p-1 rounded-md hover:bg-neutral-100 dark:hover:bg-[#222733] text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition cursor-pointer"
            title="Minimize to Pill"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="relative aspect-[16/10] bg-[#03080E] overflow-hidden flex items-center justify-center border-b border-neutral-200/60 dark:border-[#222733]">
        <PrasadamLiveCameraCanvas counter={counter} isCompact={true} />
      </div>

      <div className="p-2 bg-neutral-50/80 dark:bg-[#14171f] flex flex-col gap-1.5">
        <div className="flex items-center justify-between text-[9.5px] font-mono text-neutral-500 dark:text-neutral-400">
          <span className="flex items-center gap-1">
            <Camera className="w-3 h-3 text-blue-500" />
            <span>Switch Online Stream:</span>
          </span>
          <span className="text-emerald-600 dark:text-emerald-400 font-bold">12/12 Cams Live</span>
        </div>

        <div className="flex items-center gap-1 overflow-x-auto pb-0.5 scrollbar-none">
          {allCounters.map(c => (
            <button
              key={`dock-c-${c.id}`}
              onClick={() => onSelectCounter(c)}
              className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-bold transition cursor-pointer flex items-center gap-1 shrink-0 ${
                c.id === counter.id
                  ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/40 shadow-xs'
                  : 'bg-white dark:bg-[#181b22] text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-[#222733] border border-neutral-200/60 dark:border-[#222733]'
              }`}
              title={`Switch stream to Counter ${c.label} (${c.camId})`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span>C{c.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// DEDICATED LIVE CCTV FEED MODAL (EXPANDED MODAL)
// ============================================================================
interface PrasadamCounterCCTVFeedProps {
  counter: CounterData;
  allCounters: CounterData[];
  onClose: () => void;
  onSelectCounter: (counter: CounterData) => void;
}

const PrasadamCounterCCTVFeed: React.FC<PrasadamCounterCCTVFeedProps> = ({
  counter,
  allCounters,
  onClose,
  onSelectCounter
}) => {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-xs p-3 sm:p-5 animate-in fade-in duration-150">
      <div className="relative w-full max-w-[980px] bg-white dark:bg-[#181b22] border border-neutral-200/80 dark:border-[#262b37] rounded-xl shadow-2xl overflow-hidden flex flex-col text-neutral-800 dark:text-neutral-100">
        {/* Top Header Bar */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-neutral-50/70 dark:bg-[#14171f] border-b border-neutral-200/80 dark:border-[#262b37]">
          <div className="flex items-center gap-3">
            <span className="font-mono font-bold text-[11px] px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 shadow-xs">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>{counter.camId} · ONLINE</span>
            </span>
            <span className="font-semibold text-[13px] text-neutral-900 dark:text-neutral-100">
              {counter.id === 12
                ? 'Counter 12 (Physically Handicapped Priority Stall)'
                : `General Laddu Distribution Stall ${counter.label}`}
              <span className="text-[11px] font-mono text-neutral-500 dark:text-neutral-400 font-normal ml-2">
                Sector S-17 · Ingress &amp; Egress Camera (1080P Live Stream)
              </span>
            </span>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg bg-neutral-100 dark:bg-[#222733] hover:bg-neutral-200 dark:hover:bg-[#2c3242] border border-neutral-200/60 dark:border-[#262b37] text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition cursor-pointer flex items-center justify-center"
            title="Close Live Camera Feed"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Video Canvas Container */}
        <div className="relative aspect-[16/10] bg-[#03080E] overflow-hidden flex items-center justify-center border-b border-neutral-200/80 dark:border-[#262b37]">
          <PrasadamLiveCameraCanvas counter={counter} isCompact={false} />
        </div>

        {/* Bottom Switcher: Quick jump to other laddu counter cameras */}
        <div className="px-4 py-2.5 bg-neutral-50/70 dark:bg-[#14171f] flex flex-col gap-2">
          <div className="flex items-center justify-between text-[11px] font-mono text-neutral-500 dark:text-neutral-400">
            <span className="flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5 text-blue-500" />
              <span>Switch Live CCTV Feed to Another Counter:</span>
            </span>
            <span className="text-emerald-600 dark:text-emerald-400 font-bold">● All 12 Feeds Operational &amp; Streaming</span>
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
            {allCounters.map(c => (
              <button
                key={`cctv-btn-${c.id}`}
                onClick={() => onSelectCounter(c)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-mono font-bold transition cursor-pointer flex items-center gap-1 shrink-0 ${
                  c.id === counter.id
                    ? 'bg-blue-500/20 text-amber-600 dark:text-blue-400 border border-blue-500/40 shadow-xs'
                    : 'bg-white dark:bg-[#181b22] text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-[#222733] border border-neutral-200/60 dark:border-[#222733]'
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span>C{c.label}</span>
                <span className="text-[9.5px] opacity-75">({c.count}p)</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// =========================================================================
// MAIN PRASADAM BLUEPRINT MODAL COMPONENT
// =========================================================================
export const PrasadamBlueprintModal: React.FC<PrasadamBlueprintModalProps> = ({
  onClose,
}) => {
  const { themeMode, queueState } = useCrowd();
  const isLight = themeMode === 'light';

  // Total devotees in S-17 from live simulation
  const s17Total = queueState.xcells['XS6'] || 240;

  // Build the 12 counters data matching the blueprint image
  const buildCounters = (): CounterData[] => {
    const counters: CounterData[] = [];
    const stallW = 68;
    const baseCap = 35;

    // Counter 12: Far left, shorter stall
    const c12Count = Math.max(8, Math.round(s17Total * 0.05));
    counters.push({
      id: 12,
      label: '12',
      sublabel: 'PHYSICALLY HANDICAPPED PERSONS',
      isPriority: true,
      x: 45,
      w: stallW,
      h: 370,
      count: c12Count,
      capacity: 25,
      density: Math.min(4.0, (c12Count / 25) * 3.0),
      ratePerMin: 18,
      camId: 'CAM-S17-C12'
    });

    // Counters 11 to 6 (Left group)
    const leftIds = [11, 10, 9, 8, 7, 6];
    const leftStartX = 125;
    const remainingLeftCount = Math.round(s17Total * 0.52);

    leftIds.forEach((id, idx) => {
      const share = 0.14 + ((id * 7) % 5) / 100;
      const count = Math.max(10, Math.round(remainingLeftCount * share));
      const density = Math.min(4.2, (count / baseCap) * 3.2);

      counters.push({
        id,
        label: String(id),
        x: leftStartX + idx * (stallW + 2),
        w: stallW,
        h: 420,
        count,
        capacity: baseCap,
        density,
        ratePerMin: 28,
        camId: `CAM-S17-C${String(id).padStart(2, '0')}`
      });
    });

    // Counters 5 to 1 (Right group)
    const rightIds = [5, 4, 3, 2, 1];
    const rightStartX = 600;
    const remainingRightCount = Math.max(50, s17Total - c12Count - remainingLeftCount);

    rightIds.forEach((id, idx) => {
      const share = 0.18 + ((id * 5) % 4) / 100;
      const count = Math.max(10, Math.round(remainingRightCount * share));
      const density = Math.min(4.2, (count / baseCap) * 3.2);

      counters.push({
        id,
        label: String(id),
        x: rightStartX + idx * (stallW + 2),
        w: stallW,
        h: 420,
        count,
        capacity: baseCap,
        density,
        ratePerMin: 30,
        camId: `CAM-S17-C${String(id).padStart(2, '0')}`
      });
    });

    return counters;
  };

  const counters = buildCounters();
  const totalOccupants = counters.reduce((sum, c) => sum + c.count, 0);
  const totalCapacity = counters.reduce((sum, c) => sum + c.capacity, 0);
  const totalIssuanceRate = counters.reduce((sum, c) => sum + c.ratePerMin, 0);

  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState<boolean>(false);
  const [selectedCounter, setSelectedCounter] = useState<CounterData | null>(counters[0]);
  const [currentActiveCounter, setCurrentActiveCounter] = useState<CounterData>(counters[0]);
  const [activeOnlineCam, setActiveOnlineCam] = useState<CounterData | null>(null);
  const [showCameras, setShowCameras] = useState<boolean>(true);
  const [showFlowArrows, setShowFlowArrows] = useState<boolean>(true);
  const [showHeatmap, setShowHeatmap] = useState<boolean>(false);
  const panStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (activeOnlineCam) {
          setActiveOnlineCam(null);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeOnlineCam, onClose]);

  // Pan handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setIsPanning(true);
    panStartRef.current = { x: e.clientX - panOffset.x, y: e.clientY - panOffset.y };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isPanning) return;
    setPanOffset({
      x: e.clientX - panStartRef.current.x,
      y: e.clientY - panStartRef.current.y
    });
  };

  const handleMouseUp = () => setIsPanning(false);

  const resetView = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };

  const handleLaunchCamera = (counter: CounterData) => {
    setActiveOnlineCam(counter);
    setCurrentActiveCounter(counter);
    setSelectedCounter(counter);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/80 backdrop-blur-xs p-2 sm:p-4 animate-in fade-in duration-150"
      id="prasadamBlueprintModal"
      onClick={(e) => {
        if ((e.target as HTMLElement).id === 'prasadamBlueprintModal') onClose();
      }}
    >
      <div className="relative w-full max-w-[1550px] h-[94vh] bg-white dark:bg-[#181b22] border border-neutral-200/80 dark:border-[#262b37] rounded-xl flex flex-col overflow-hidden shadow-2xl text-neutral-800 dark:text-neutral-100 transition-colors duration-200">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-3.5 sm:right-4 z-30 p-1.5 rounded-lg bg-neutral-100 dark:bg-[#222733] hover:bg-neutral-200 dark:hover:bg-[#2c3242] border border-neutral-200/60 dark:border-[#262b37] text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition cursor-pointer flex items-center justify-center shadow-md active:scale-95"
          title="Close Blueprint Modal"
          aria-label="Close Blueprint Modal"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Top Header */}
        <div className="shrink-0 px-4 py-2.5 pr-14 sm:pr-16 bg-neutral-50/80 dark:bg-[#14171f] border-b border-neutral-200/80 dark:border-[#262b37] flex flex-wrap items-center justify-between gap-3 z-20">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/15 border border-blue-500/30 text-amber-600 dark:text-blue-400">
              <ShoppingBag className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-[14px] font-bold uppercase tracking-wide text-neutral-900 dark:text-neutral-100">
                  S-17 Prasadam &amp; Laddu Counters — Operational Blueprint
                </h2>
                <span className="font-mono text-[9px] px-2 py-0.5 rounded bg-blue-500/15 border border-blue-500/30 text-amber-700 dark:text-amber-300 font-bold">
                  Sector S-17 · XS6
                </span>
                <span className="font-mono text-[9px] px-2 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1.5 shadow-xs">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  12 / 12 CAMS ONLINE
                </span>
              </div>
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400 font-mono mt-0.5">
                ప్రసాదం &amp; లడ్డూ కౌంటర్లు · Physically Handicapped Priority Counter 12 · Laddu Vehicle Movement &amp; Storage Array
              </p>
            </div>
          </div>

          {/* Metrics pills */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="px-2.5 py-1 rounded-lg bg-white dark:bg-[#181b22] border border-neutral-200/60 dark:border-[#222733] text-right shadow-xs">
              <span className="text-[8.5px] font-mono text-neutral-500 dark:text-neutral-400 uppercase font-semibold">Devotees in S-17</span>
              <div className="font-mono text-[12px] font-bold text-blue-600 dark:text-blue-400 leading-tight">
                {fmt(totalOccupants)}{' '}
                <span className="text-[9.5px] text-neutral-400 dark:text-neutral-500 font-normal">/ {fmt(totalCapacity)}</span>
              </div>
            </div>

            <div className="px-2.5 py-1 rounded-lg bg-white dark:bg-[#181b22] border border-neutral-200/60 dark:border-[#222733] text-right shadow-xs">
              <span className="text-[8.5px] font-mono text-neutral-500 dark:text-neutral-400 uppercase font-semibold">Distribution Rate</span>
              <div className="font-mono text-[12px] font-bold text-amber-600 dark:text-blue-400 leading-tight">
                {totalIssuanceRate} <span className="text-[9.5px] text-neutral-400 dark:text-neutral-500 font-normal">/min</span>
              </div>
            </div>

            <div className="px-2.5 py-1 rounded-lg bg-white dark:bg-[#181b22] border border-neutral-200/60 dark:border-[#222733] text-right shadow-xs">
              <span className="text-[8.5px] font-mono text-neutral-500 dark:text-neutral-400 uppercase font-semibold">Camera Status</span>
              <div className="font-mono text-[12px] font-bold text-emerald-600 dark:text-emerald-400 leading-tight flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span>ONLINE (12)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar & Layer Toggles */}
        <div className="shrink-0 px-4 py-1.5 bg-neutral-50/60 dark:bg-[#151821] border-b border-neutral-200/60 dark:border-[#222733] flex flex-wrap items-center justify-between gap-2 z-10 text-[11px]">
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => setShowCameras(c => !c)}
              className={`px-2 py-0.8 rounded-md font-mono text-[10px] border transition flex items-center gap-1.5 cursor-pointer ${
                showCameras
                  ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-600 dark:text-emerald-400 font-semibold'
                  : 'text-neutral-500 border-neutral-200 dark:border-[#262b37] hover:bg-neutral-100 dark:hover:bg-[#222733]'
              }`}
            >
              <Camera className="w-3 h-3" />
              <span>CCTV Online (12/12)</span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            </button>

            <button
              onClick={() => setShowFlowArrows(f => !f)}
              className={`px-2 py-0.8 rounded-md font-mono text-[10px] border transition flex items-center gap-1 cursor-pointer ${
                showFlowArrows ? 'bg-rose-500/15 border-rose-500/40 text-rose-600 dark:text-rose-400 font-semibold' : 'text-neutral-500 border-neutral-200 dark:border-[#262b37] hover:bg-neutral-100 dark:hover:bg-[#222733]'
              }`}
            >
              <Eye className="w-3 h-3" />
              <span>In / Out Flow Arrows</span>
            </button>

            <button
              onClick={() => setShowHeatmap(h => !h)}
              className={`px-2 py-0.8 rounded-md font-mono text-[10px] border transition flex items-center gap-1 cursor-pointer ${
                showHeatmap ? 'bg-blue-500/15 border-blue-500/40 text-amber-700 dark:text-amber-300 font-semibold' : 'text-neutral-500 border-neutral-200 dark:border-[#262b37] hover:bg-neutral-100 dark:hover:bg-[#222733]'
              }`}
            >
              <Flame className="w-3 h-3" />
              <span>Queue Density Heatmap</span>
            </button>
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center gap-1.5">
            <span className="text-[9.5px] font-mono text-neutral-500 dark:text-neutral-400">
              Zoom: {Math.round(zoomLevel * 100)}%
            </span>
            <button
              onClick={() => setZoomLevel(z => Math.min(2.4, z + 0.15))}
              className="p-1 rounded-md border bg-white dark:bg-[#181b22] border-neutral-200/80 dark:border-[#262b37] text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition cursor-pointer"
              title="Zoom In"
            >
              <ZoomIn className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setZoomLevel(z => Math.max(0.6, z - 0.15))}
              className="p-1 rounded-md border bg-white dark:bg-[#181b22] border-neutral-200/80 dark:border-[#262b37] text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition cursor-pointer"
              title="Zoom Out"
            >
              <ZoomOut className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={resetView}
              className="p-1 rounded-md border bg-white dark:bg-[#181b22] border-neutral-200/80 dark:border-[#262b37] text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition cursor-pointer"
              title="Reset View"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Blueprint SVG Canvas Area */}
        <div
          className="flex-1 relative overflow-hidden bg-[#f4f5f8] dark:bg-[#0e1015] cursor-grab active:cursor-grabbing flex items-center justify-center p-2 select-none"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div
            className="w-full h-full flex items-center justify-center will-change-transform"
            style={{
              transform: `translate3d(${panOffset.x}px, ${panOffset.y}px, 0) scale(${zoomLevel})`,
              transformOrigin: 'center center'
            }}
          >
            <svg
              viewBox="0 0 1020 540"
              className="w-full h-full block max-h-full max-w-full drop-shadow-xl"
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                {/* Subtle drafting grid */}
                <pattern id="prasadamGrid" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke={isLight ? "#F1F5F9" : "#081B2B"} strokeWidth="0.6" />
                </pattern>

                {/* Red Marker Arrow definition */}
                <marker id="redArrowP" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path d="M 0 1 L 10 5 L 0 9 z" fill="#E53E3E" />
                </marker>

                <filter id="prasadamGlow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="4" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>

                {/* Camera Surveillance Field of View (FOV) Gradients */}
                <linearGradient id="camFovNormal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity="0.42" />
                  <stop offset="55%" stopColor="#10B981" stopOpacity="0.10" />
                  <stop offset="100%" stopColor="#10B981" stopOpacity="0.01" />
                </linearGradient>
                <linearGradient id="camFovActive" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#34D399" stopOpacity="0.70" />
                  <stop offset="55%" stopColor="#10B981" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#10B981" stopOpacity="0.04" />
                </linearGradient>
              </defs>

              {/* Canvas Background */}
              <rect width="1020" height="540" fill={isLight ? "#FFFFFF" : "#040D18"} />
              <rect width="1020" height="540" fill="url(#prasadamGrid)" />

              {/* Outer Border Frame */}
              <rect x="5" y="5" width="1010" height="530" fill="none" stroke={isLight ? "#000000" : "#1A365D"} strokeWidth="1" />

              {/* ================= TOP STORAGE AREA TEXTS ================= */}
              {/* Above Counters 11 to 6 */}
              <g id="storageAreaLeft" transform="translate(345, 45)">
                <text x="0" y="0" textAnchor="middle" fill={isLight ? "#000000" : "#E2E8F0"} fontFamily='"Outfit", sans-serif' fontSize="13" fontWeight="bold">
                  laddu trays
                </text>
                <text x="0" y="16" textAnchor="middle" fill={isLight ? "#000000" : "#E2E8F0"} fontFamily='"Outfit", sans-serif' fontSize="13" fontWeight="bold">
                  storage area
                </text>
              </g>

              {/* Above Counters 5 to 1 */}
              <g id="storageAreaRight" transform="translate(775, 135)">
                <text x="0" y="0" textAnchor="middle" fill={isLight ? "#000000" : "#E2E8F0"} fontFamily='"Outfit", sans-serif' fontSize="13" fontWeight="bold">
                  laddu trays
                </text>
                <text x="0" y="16" textAnchor="middle" fill={isLight ? "#000000" : "#E2E8F0"} fontFamily='"Outfit", sans-serif' fontSize="13" fontWeight="bold">
                  storage area
                </text>
              </g>

              {/* ================= MIDDLE VEHICLE MOVEMENT AREA ================= */}
              <g id="vehicleMovementArea" transform="translate(565, 340)">
                <text x="0" y="0" textAnchor="middle" fill={isLight ? "#000000" : "#E2E8F0"} fontFamily='"Outfit", sans-serif' fontSize="13" fontWeight="bold">
                  laddu
                </text>
                <text x="0" y="16" textAnchor="middle" fill={isLight ? "#000000" : "#E2E8F0"} fontFamily='"Outfit", sans-serif' fontSize="13" fontWeight="bold">
                  vehical
                </text>
                <text x="0" y="32" textAnchor="middle" fill={isLight ? "#000000" : "#E2E8F0"} fontFamily='"Outfit", sans-serif' fontSize="13" fontWeight="bold">
                  moment
                </text>
                <text x="0" y="48" textAnchor="middle" fill={isLight ? "#000000" : "#E2E8F0"} fontFamily='"Outfit", sans-serif' fontSize="13" fontWeight="bold">
                  area
                </text>
              </g>

              {/* ================= 12 COUNTERS RENDERING ================= */}
              {counters.map(counter => {
                const isSelected = selectedCounter?.id === counter.id;
                const isCameraActive = currentActiveCounter?.id === counter.id;
                const occ = counter.capacity > 0 ? Math.round((counter.count / counter.capacity) * 100) : 0;
                const isDense = occ >= 70;
                const isFull = occ >= 90;
                const col = losColor(counter.density);

                const stallFill = isLight
                  ? (isSelected ? '#FEF3C7' : isFull ? '#FEE2E2' : isDense ? '#FFFBEB' : '#FFFFFF')
                  : (isSelected ? '#2A1F0D' : isFull ? '#2C1217' : isDense ? '#1F180A' : '#081624');

                const stallStroke = isLight
                  ? (isSelected ? '#D97706' : isCameraActive ? '#059669' : '#000000')
                  : (isSelected ? '#F59E0B' : isCameraActive ? '#10B981' : '#1E4976');

                // Channel dimensions
                const stallTopY = counter.id === 12 ? 175 : 95;
                const stallBottomY = 475;
                const laneW = counter.w / 2;
                const centerDividerTop = counter.id === 12 ? 215 : 135;

                return (
                  <g
                    key={`counter-${counter.id}`}
                    className="cursor-pointer group"
                    onClick={() => {
                      setSelectedCounter(counter);
                      setCurrentActiveCounter(counter);
                    }}
                  >
                    {/* Top Counter Box */}
                    {counter.id === 12 ? (
                      // Counter 12: Special PH box
                      <g transform="translate(45, 145)">
                        <rect
                          x="13"
                          y="0"
                          width="42"
                          height="30"
                          fill={isLight ? "#FFFFFF" : "#0D2235"}
                          stroke={stallStroke}
                          strokeWidth={isSelected || isCameraActive ? '2' : '1.2'}
                        />
                        <text x="34" y="11" textAnchor="middle" fill={isLight ? "#000000" : "#FFFFFF"} fontFamily='"Outfit", sans-serif' fontSize="10" fontWeight="bold">
                          12
                        </text>
                        <text x="34" y="17" textAnchor="middle" fill={isLight ? "#000000" : "#A0AEC0"} fontFamily='"Outfit", sans-serif' fontSize="4.5" fontWeight="bold">
                          PHYSICALLY
                        </text>
                        <text x="34" y="22" textAnchor="middle" fill={isLight ? "#000000" : "#A0AEC0"} fontFamily='"Outfit", sans-serif' fontSize="4" fontWeight="bold">
                          HANDICAPPED
                        </text>
                        <text x="34" y="27" textAnchor="middle" fill={isLight ? "#000000" : "#A0AEC0"} fontFamily='"Outfit", sans-serif' fontSize="4" fontWeight="bold">
                          PERSONS
                        </text>
                      </g>
                    ) : (
                      // Standard Counter Box on top of stall
                      <g transform={`translate(${counter.x + (counter.w - 42) / 2}, ${stallTopY - 26})`}>
                        <rect
                          x="0"
                          y="0"
                          width="42"
                          height="26"
                          fill={isLight ? "#FFFFFF" : "#0D2235"}
                          stroke={stallStroke}
                          strokeWidth={isSelected || isCameraActive ? '2' : '1.2'}
                        />
                        <text x="21" y="18" textAnchor="middle" fill={isLight ? "#000000" : "#FFFFFF"} fontFamily='"Outfit", sans-serif' fontSize="15" fontWeight="bold">
                          {counter.label}
                        </text>
                      </g>
                    )}

                    {/* Main Stall Outer Rectangle */}
                    <rect
                      x={counter.x}
                      y={stallTopY}
                      width={counter.w}
                      height={stallBottomY - stallTopY}
                      fill={stallFill}
                      stroke={stallStroke}
                      strokeWidth={isSelected || isCameraActive ? '2' : '1.2'}
                    />

                    {/* Camera Field-of-View (FOV) Surveillance Vision Cone */}
                    {showCameras && (
                      <g id={`fov-${counter.id}`} pointerEvents="none">
                        <polygon
                          points={`${counter.x + counter.w - 15},${stallTopY + 14} ${counter.x - 3},${stallBottomY} ${counter.x + counter.w + 3},${stallBottomY}`}
                          fill={isCameraActive || isSelected ? "url(#camFovActive)" : "url(#camFovNormal)"}
                        />
                        {/* Live Optical Scanning Sweep Beam */}
                        <line
                          x1={counter.x}
                          y1={stallTopY + 25}
                          x2={counter.x + counter.w}
                          y2={stallTopY + 25}
                          stroke={isCameraActive || isSelected ? "#34D399" : "#10B981"}
                          strokeWidth={isCameraActive || isSelected ? "1.8" : "1.2"}
                        >
                          <animate
                            attributeName="y1"
                            values={`${stallTopY + 25};${stallBottomY - 15};${stallTopY + 25}`}
                            dur={`${4.2 + (counter.id % 4) * 0.4}s`}
                            repeatCount="indefinite"
                          />
                          <animate
                            attributeName="y2"
                            values={`${stallTopY + 25};${stallBottomY - 15};${stallTopY + 25}`}
                            dur={`${4.2 + (counter.id % 4) * 0.4}s`}
                            repeatCount="indefinite"
                          />
                          <animate
                            attributeName="opacity"
                            values="0.25;0.9;0.25"
                            dur={`${4.2 + (counter.id % 4) * 0.4}s`}
                            repeatCount="indefinite"
                          />
                        </line>
                      </g>
                    )}

                    {/* Heatmap overlay if toggled */}
                    {showHeatmap && (
                      <rect
                        x={counter.x}
                        y={stallTopY}
                        width={counter.w}
                        height={stallBottomY - stallTopY}
                        fill={col}
                        opacity={isLight ? "0.2" : "0.35"}
                        filter="url(#prasadamGlow)"
                      />
                    )}

                    {/* Center Divider Wall between 'in' and 'out' (stops before top for U-turn loop) */}
                    <line
                      x1={counter.x + laneW}
                      y1={centerDividerTop}
                      x2={counter.x + laneW}
                      y2={stallBottomY}
                      stroke={stallStroke}
                      strokeWidth="1.2"
                    />

                    {/* ================= FLOW ARROWS ================= */}
                    {showFlowArrows && (
                      <g id={`arrows-${counter.id}`}>
                        {/* 1. UPWARD arrows on 'in' (right) lane */}
                        <line
                          x1={counter.x + laneW * 1.5}
                          y1={stallBottomY - 30}
                          x2={counter.x + laneW * 1.5}
                          y2={stallBottomY - 80}
                          stroke="#E53E3E"
                          strokeWidth="1.2"
                          markerEnd="url(#redArrowP)"
                        />
                        <line
                          x1={counter.x + laneW * 1.5}
                          y1={stallBottomY - 110}
                          x2={counter.x + laneW * 1.5}
                          y2={stallBottomY - 170}
                          stroke="#E53E3E"
                          strokeWidth="1.2"
                          markerEnd="url(#redArrowP)"
                        />
                        <line
                          x1={counter.x + laneW * 1.5}
                          y1={stallBottomY - 200}
                          x2={counter.x + laneW * 1.5}
                          y2={centerDividerTop + 20}
                          stroke="#E53E3E"
                          strokeWidth="1.2"
                          markerEnd="url(#redArrowP)"
                        />

                        {/* 2. Curved U-turn arrow at top */}
                        <path
                          d={`M ${counter.x + laneW * 1.5} ${centerDividerTop + 20} Q ${counter.x + laneW * 1.5} ${centerDividerTop - 25} ${counter.x + laneW} ${centerDividerTop - 25} Q ${counter.x + laneW * 0.5} ${centerDividerTop - 25} ${counter.x + laneW * 0.5} ${centerDividerTop + 15}`}
                          fill="none"
                          stroke="#E53E3E"
                          strokeWidth="1.2"
                          markerEnd="url(#redArrowP)"
                        />

                        {/* 3. DOWNWARD arrows on 'out' (left) lane */}
                        <line
                          x1={counter.x + laneW * 0.5}
                          y1={centerDividerTop + 35}
                          x2={counter.x + laneW * 0.5}
                          y2={stallBottomY - 230}
                          stroke="#E53E3E"
                          strokeWidth="1.2"
                          markerEnd="url(#redArrowP)"
                        />
                        <line
                          x1={counter.x + laneW * 0.5}
                          y1={stallBottomY - 210}
                          x2={counter.x + laneW * 0.5}
                          y2={stallBottomY - 130}
                          stroke="#E53E3E"
                          strokeWidth="1.2"
                          markerEnd="url(#redArrowP)"
                        />
                        <line
                          x1={counter.x + laneW * 0.5}
                          y1={stallBottomY - 110}
                          x2={counter.x + laneW * 0.5}
                          y2={stallBottomY - 25}
                          stroke="#E53E3E"
                          strokeWidth="1.2"
                          markerEnd="url(#redArrowP)"
                        />
                      </g>
                    )}

                    {/* Bottom Lane Labels: 'out' and 'in' */}
                    <text
                      x={counter.x + laneW * 0.5}
                      y={stallBottomY + 22}
                      textAnchor="middle"
                      fill={isLight ? "#000000" : "#E2E8F0"}
                      fontFamily='"Outfit", sans-serif'
                      fontSize="14"
                      fontWeight="bold"
                    >
                      out
                    </text>
                    <text
                      x={counter.x + laneW * 1.5}
                      y={stallBottomY + 22}
                      textAnchor="middle"
                      fill={isLight ? "#000000" : "#E2E8F0"}
                      fontFamily='"Outfit", sans-serif'
                      fontSize="14"
                      fontWeight="bold"
                    >
                      in
                    </text>

                    {/* Live queue count badge inside stall */}
                    <rect
                      x={counter.x + 8}
                      y={stallBottomY - 36}
                      width={counter.w - 16}
                      height="15"
                      rx="2"
                      fill={isLight ? "#F8FAFC" : "#0D2638"}
                      stroke={isLight ? "#CBD5E1" : "#1A3D5E"}
                      strokeWidth="0.8"
                    />
                    <text
                      x={counter.x + counter.w / 2}
                      y={stallBottomY - 25}
                      textAnchor="middle"
                      fill={isLight ? "#0F172A" : "#FFFFFF"}
                      fontFamily='"Inter", sans-serif'
                      fontSize="9"
                      fontWeight="bold"
                    >
                      {counter.count} <tspan fill={isLight ? "#64748B" : "#718096"}>pax</tspan>
                    </text>

                    {/* CCTV Camera ONLINE Icon & Pulsing Beacon */}
                    {showCameras && (
                      <g
                        transform={`translate(${counter.x + counter.w - 15}, ${stallTopY + 14})`}
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLaunchCamera(counter);
                        }}
                      >
                        {/* Camera background circle */}
                        <circle
                          cx="0"
                          cy="0"
                          r="8"
                          fill={isCameraActive ? "#064E3B" : isLight ? "#EFF6FF" : "#1E293B"}
                          stroke="#10B981"
                          strokeWidth={isCameraActive ? "2" : "1.2"}
                        />
                        <Cctv x={-4.5} y={-4.5} width={9} height={9} color={isCameraActive ? "#34D399" : isLight ? "#059669" : "#34D399"} />

                        {/* Pulsing Green ONLINE Indicator */}
                        <circle cx="5" cy="-5" r="2.2" fill="#10B981">
                          <animate attributeName="opacity" values="1;0.2;1" dur="0.8s" repeatCount="indefinite" />
                          <animate attributeName="r" values="2;3.2;2" dur="0.8s" repeatCount="indefinite" />
                        </circle>

                        {/* ONLINE text pill */}
                        <rect x="-18" y="10" width="36" height="11" rx="2" fill={isCameraActive ? "#059669" : "#065F46"} stroke="#10B981" strokeWidth="0.6" />
                        <text x="0" y="18.5" textAnchor="middle" fill="#A7F3D0" fontFamily="monospace" fontSize="6.5" fontWeight="bold">
                          ONLINE
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Picture-in-Picture Docked Live Online CCTV Stream */}
          {showCameras && (
            <PrasadamDockedCameraFeed
              counter={currentActiveCounter}
              allCounters={counters}
              onSelectCounter={(c) => {
                setCurrentActiveCounter(c);
                setSelectedCounter(c);
              }}
              onMaximize={(c) => {
                handleLaunchCamera(c);
              }}
            />
          )}
        </div>

        {/* Bottom Inspector Bar & Selected Counter Details */}
        <div className="shrink-0 px-4 py-2 bg-neutral-50/80 dark:bg-[#14171f] border-t border-neutral-200/80 dark:border-[#262b37] flex flex-wrap items-center justify-between gap-3 z-20">
          {selectedCounter ? (
            <div className="flex items-center gap-4 flex-wrap w-full justify-between">
              <div className="flex items-center gap-3">
                <div className="px-2.5 py-1 rounded-lg bg-blue-500/15 border border-blue-500/30 text-amber-600 dark:text-blue-400 font-mono text-[13px] font-bold flex items-center gap-1.5">
                  <span>Counter {selectedCounter.label}</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                </div>
                <div>
                  <div className="text-[12px] font-bold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
                    <span>
                      {selectedCounter.id === 12
                        ? 'Counter 12 · Physically Handicapped Priority'
                        : `General Distribution Stall ${selectedCounter.label}`}
                    </span>
                    <span className="font-mono text-[11px] px-1.5 py-0.2 rounded bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-bold">
                      CCTV ONLINE
                    </span>
                    <span className="font-mono text-[11px] px-1.5 py-0.2 rounded bg-neutral-200/70 dark:bg-[#222733] text-neutral-700 dark:text-neutral-300">
                      Rate: {selectedCounter.ratePerMin} laddus/min
                    </span>
                    <span className="text-[11px] font-mono text-neutral-500 dark:text-neutral-400">
                      {selectedCounter.count} Devotees in Channel ({Math.round((selectedCounter.count / selectedCounter.capacity) * 100)}% Saturation)
                    </span>
                  </div>
                  <div className="text-[10px] font-mono text-neutral-400 dark:text-neutral-500 flex items-center gap-2 mt-0.5">
                    <span>Density: {selectedCounter.density.toFixed(1)} p/m² ({crowdWord(selectedCounter.density)})</span>
                    <span>·</span>
                    <span>Flow: Separate Ingress &amp; Egress Lanes with U-Turn</span>
                    <span>·</span>
                    <span>Camera ID: {selectedCounter.camId} (1080P Live AI Stream)</span>
                  </div>
                </div>
              </div>

              {/* Actions for Selected Counter */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleLaunchCamera(selectedCounter)}
                  className="px-3.5 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-600 dark:text-emerald-400 text-[11px] font-mono font-bold uppercase tracking-wider flex items-center gap-1.5 transition cursor-pointer active:scale-95 shadow-xs"
                >
                  <Video className="w-3.5 h-3.5 text-emerald-500" />
                  <span>View Online Camera Feed</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse ml-1" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between w-full text-[11px] text-neutral-500 dark:text-neutral-400 font-mono">
              <span className="flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5 text-blue-500" />
                Click on any of the 12 counter camera icons to view the live online CCTV video stream with real-time AI crowd bounding boxes.
              </span>
              <span className="text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                12 Cameras Online · Live AI Computer Vision Streaming
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ================= DEDICATED LIVE ONLINE CCTV FEED MODAL ================= */}
      {activeOnlineCam && (
        <PrasadamCounterCCTVFeed
          counter={activeOnlineCam}
          allCounters={counters}
          onClose={() => setActiveOnlineCam(null)}
          onSelectCounter={(newCounter) => setActiveOnlineCam(newCounter)}
        />
      )}
    </div>
  );
};
